import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { v4 } from 'uuid';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { BackendFeatureFlags } from '../../../../../../typings/backend-feature-flags';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { iiafe } from '../../../../utils/async';
import { isDefined } from '../../../../utils/variables';
import { SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { useSubscribe } from '../../../hooks/use-subscribe';
import { ProjectContext } from '../../../state/project-state/project-state';
import { Center } from '../../../styles/center';
import { Icon } from '../../icon/icon';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { translatedProcessType } from '../geodata';
import { GeodataSetupButton } from '../geodata-setup/geodata-setup-button';
import { GeodataListContent } from './geodata-list-content';
import { GeodataListItem } from './geodata-list-item';
import { NoGeodata } from './no-geodata';

const sortByUploadedDesc = (a: GeodataModel, b: GeodataModel) =>
  new Date(a.uploaded) < new Date(b.uploaded) ? 1 : -1;

function useProjectGeodataQuery(projectId: string) {
  const { handleError, clearErrors, buildErrorList } = useErrorHandling();
  const [queryId] = useState(v4());
  const queryClient = useQueryClient();
  const queryKey = ['getProjectGeodata', projectId, queryId];

  const { data, refetch, isPending, isRefetching, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceV1()
        .getProjectGeodata({
          path: { projectId },
          query: { include: ['geodata.gcpCorrectionJobRequest'] },
          headers: {
            'x-flag':
              'SK-6337-exclude-geodata-include-options-by-default' satisfies BackendFeatureFlags,
          },
        });
      return response.data;
    },
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useSubscribe(SubscriptionTopic.WebsocketMessageRecieved, (event) => {
    if (
      (event.message === 'GeodataDeleted' ||
        event.message === 'GeodataCreated' ||
        event.message === 'GeodataUpdated') &&
      event.projectId === projectId
    ) {
      iiafe(async () => {
        const currentData = queryClient.getQueryData<GeodataModel[]>(queryKey);
        if (!isDefined(currentData)) {
          return;
        }

        if (event.message === 'GeodataDeleted') {
          const deletedEvent = event;
          queryClient.setQueryData<GeodataModel[]>(queryKey, (oldData) => {
            if (!oldData) {
              return oldData;
            }
            return oldData.filter((geodata) => geodata.id !== deletedEvent.geodataId);
          });
        } else if (event.message === 'GeodataCreated') {
          // Just refetch if a new item is created.
          await refetch();
        } else if (event.message === 'GeodataUpdated') {
          const updatedEvent = event;
          if (updatedEvent.gcpCorrectionRequested !== undefined) {
            // gcpCorrectionJobRequest relation is not in the websocket payload; refetch to keep it fresh.
            await refetch();
            return;
          }
          queryClient.setQueryData<GeodataModel[]>(queryKey, (oldData) => {
            if (!oldData) {
              return oldData;
            }
            return oldData.map((geodata) => {
              if (geodata.id !== updatedEvent.geodataId) {
                return geodata;
              }
              return {
                ...geodata,
                name: updatedEvent.geodataName,
                processType: updatedEvent.processType,
                status: updatedEvent.geodataStatus as GeodataModel['status'],
              };
            });
          });
        }
      });
    }
  });

  // Define effect on error outside of mutation to avoid infinite render loop.
  useEffect(() => {
    if (isDefined(error)) {
      handleError(error, { retryCallback: refetch });
    } else {
      clearErrors();
    }
  }, [handleError, clearErrors, error, refetch]);

  const geodataList = useMemo(() => {
    return data?.sort(sortByUploadedDesc);
  }, [data]);

  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);

  return {
    geodataList,
    errorList,
    isPending: isPending || isRefetching,
    refetch,
  };
}

const GeodataList = () => {
  const { t } = useTranslation();
  const { project } = React.useContext(ProjectContext);
  const theme = useTheme();
  const [items, setItems] = React.useState<GeodataModel[]>([]);
  const [selectedItem, setSelectedItem] = React.useState<GeodataModel>();
  const { geodataList, errorList, refetch, isPending } = useProjectGeodataQuery(project.id);
  const [searchText, setSearchText] = useState('');

  const onDeleteDialogClosed = useCallback(
    (wasDeleted: boolean) => {
      if (wasDeleted && selectedItem) {
        const newItems = [...items];
        const index = newItems.indexOf(selectedItem);
        if (index >= 0) {
          newItems.splice(index, 1);
        }

        setItems(newItems.sort(sortByUploadedDesc));
        setSelectedItem(undefined);
      }
    },
    [items, selectedItem],
  );

  useEffect(() => {
    if (selectedItem) {
      setSelectedItem(items.find((x) => x.id === selectedItem.id));
    }
  }, [items, selectedItem]);

  useEffect(() => {
    if (isDefined(geodataList)) {
      setItems(geodataList.slice());
    }
  }, [geodataList]);

  const filteredItems = useMemo(() => {
    const itemsWithDisplayProcessType = items.map((item) => ({
      geodata: item,
      displayProcessType: translatedProcessType(item.processType, t).toUpperCase(),
    }));

    const array = itemsWithDisplayProcessType.filter((item) =>
      [item.geodata.name, item.displayProcessType].some((x) =>
        x.toLowerCase().includes(searchText.toLowerCase()),
      ),
    );

    return array.map((item) => (
      <GeodataListItem
        item={item.geodata}
        key={item.geodata.id}
        selected={selectedItem?.id === item.geodata.id}
        translatedProcessType={item.displayProcessType}
        onClick={() => setSelectedItem(item.geodata)}
        onDeleteDialogClosed={onDeleteDialogClosed}
      />
    ));
  }, [items, searchText, onDeleteDialogClosed, selectedItem, t]);

  return (
    <Component direction="column" spacing={1}>
      <SearchBar direction="row" justifyContent="space-between" spacing={2}>
        <TextBox
          padding={0.25}
          placeholder={t('search', { ns: 'common' })}
          value={searchText}
          width={250}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Stack alignItems="center" direction="row" spacing={1}>
          <GeodataSetupButton onCloseDialog={refetch} />
          <Icon
            color={theme.color.gray.dark}
            icon={isPending ? ['fas', 'spinner'] : ['fad', 'arrow-rotate-right']}
            size="lg"
            spin={isPending}
            title={t('refreshList', { ns: 'common' })}
            onClick={() => {
              void refetch();
            }}
            onHoverStyle={{ icon: ['fas', isPending ? 'spinner' : 'arrow-rotate-right'] }}
          />
        </Stack>
      </SearchBar>
      {!errorList && (
        <ListContainer direction="row" spacing={2}>
          {!isDefined(geodataList) ? (
            <Center>{t('oneMoment', { ns: 'common' })}</Center>
          ) : (
            <>
              <LeftSide>{filteredItems}</LeftSide>
              <RightSide>
                {items.length === 0 ? (
                  <NoGeodata />
                ) : (
                  <GeodataListContent items={items} selectedItem={selectedItem} />
                )}
              </RightSide>
            </>
          )}
        </ListContainer>
      )}
      {errorList}
    </Component>
  );
};

const Component = styled(Stack)`
  width: 100%;
`;

const SearchBar = styled(Stack)`
  width: 100%;
`;

const ListContainer = styled(Stack)`
  height: 35em;
`;

const LeftSide = styled.div`
  width: 20em;
  overflow-y: auto;
`;

const RightSide = styled.div`
  flex: 1;
  overflow-y: auto;
`;

export { GeodataList };
